
  import { defineComponent, markRaw } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import DownloadButton from '@/common/components/redesigned/DownloadButton.vue';
  import type { TFilter, TFilterConfig } from '@/common/components/redesigned/Filter/types/filter';
  import PageTitle from '@/common/components/redesigned/PageTitle.vue';
  import StatusBadge from '@/common/components/redesigned/StatusBadge.vue';
  import TableWithOuterFilter from '@/common/components/redesigned/Table/TableWithOuterFilter.vue';
  import type { TTableData, TTableWithOuterFilterConfig } from '@/common/components/redesigned/Table/types/tableTypes';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { mapSortParams } from '@/common/utils/sort';

  import IndicatorNameCell from '../components/indicatorLoadMonitoring/IndicatorNameCell.vue';
  import { UPDATE_STATUS_TYPE } from '../constants/indicatorLoadMonitoring';
  import {
    INDICATOR_LOAD_MONITORING_ACTIONS_NAME,
    INDICATOR_LOAD_MONITORING_GETTERS_NAME,
    INDICATOR_LOAD_MONITORING_MUTATIONS_NAME,
  } from '../store/indicatorLoadMonitoring/name';
  import type { TIndicatorRow } from '../store/indicatorLoadMonitoring/types';

  import Link from '@/utils/links';

  const REPORT_FILENAME = 'Мониторинг_загрузки_показателей.xlsx';

  export default defineComponent({
    name: 'IndicatorLoadMonitoring',
    components: { PageTitle, TableWithOuterFilter, DownloadButton },
    mixins: [security],
    data(): {
      isDownloading: boolean;
      downloadMessage: string;
      canExport: boolean;
    } {
      return {
        isDownloading: false,
        downloadMessage: '',
        canExport: false,
      };
    },
    created() {
      this.checkPermissionAndRedirect(Constants.Permissions.IndicatorLoadMonitoringView).then(
        async (isAllowed: boolean) => {
          if (!isAllowed) return;

          const exportResponse = await this.checkPermission(Constants.Permissions.IndicatorLoadMonitoringExport);
          this.canExport = exportResponse.data;

          this.getUpdateStatuses();
          this.getSources();
        },
      );
    },
    computed: {
      ...mapGetters({
        getStatusOptions: INDICATOR_LOAD_MONITORING_GETTERS_NAME.getUpdateStatuses,
        getSourceOptions: INDICATOR_LOAD_MONITORING_GETTERS_NAME.getSources,
        getLoadError: INDICATOR_LOAD_MONITORING_GETTERS_NAME.getLoadError,
      }),
      getEmptyText(): string {
        return this.getLoadError ? 'Сервер недоступен' : 'Нет данных для отображения';
      },

      getBaseTableConfig(): Pick<TTableWithOuterFilterConfig<TIndicatorRow>, 'columns' | 'storePaths'> {
        return {
          columns: [
            {
              field: 'updateStatus',
              title: 'Статус',
              width: 160,
              renderComponent: {
                component: markRaw(StatusBadge),
                props: (row) => ({
                  name: row.updateStatus.name,
                  type: UPDATE_STATUS_TYPE[row.updateStatus.id] ?? 'grey',
                }),
              },
            },
            {
              field: 'name',
              title: 'Показатель',
              width: 260,
              sortable: true,
              renderComponent: {
                component: markRaw(IndicatorNameCell),
                props: (row) => ({ name: row.name?.title, tooltip: row.name?.tooltip }),
              },
            },
            { field: 'updateDate', title: 'Время и дата последней загрузки', width: 200, sortable: true },
            { field: 'plannedUpdateDate', title: 'Плановая дата обновления', width: 160 },
            {
              field: 'source',
              title: 'Источник данных',
              width: 220,
              formatter: (row) => row.source.name,
            },
            { field: 'updateFrequency', title: 'Плановая периодичность обновления', width: 220 },
          ],
          storePaths: {
            action: INDICATOR_LOAD_MONITORING_ACTIONS_NAME.getMonitoringPage,
            mutation: INDICATOR_LOAD_MONITORING_MUTATIONS_NAME.setMonitoringPage,
            getter: INDICATOR_LOAD_MONITORING_GETTERS_NAME.getMonitoringPage,
          },
        };
      },
      getTableConfig(): TTableWithOuterFilterConfig<TIndicatorRow> {
        return {
          ...this.getBaseTableConfig,
          pagination: { size: 20 },
          emptyText: this.getEmptyText,
          rowClass: this.getRowClass,
        };
      },
      getFiltersConfig(): TFilterConfig[] {
        return [
          {
            type: 'text',
            label: 'Наименование показателя',
            vModel: 'name',
            clearable: true,
          },
          {
            type: 'select',
            label: 'Статус',
            vModel: 'updateStatusId',
            options: this.getStatusOptions,
            valueProp: 'id',
            labelProp: 'name',
            clearable: true,
          },
          { type: 'date', label: 'Дата загрузки', vModel: 'updateDate', typeable: true, hasInputMask: true },
          {
            type: 'select',
            label: 'Источник',
            vModel: 'sourceId',
            options: this.getSourceOptions,
            valueProp: 'id',
            labelProp: 'name',
            clearable: true,
          },
          {
            type: 'date',
            label: 'Плановая дата обновления',
            vModel: 'plannedUpdateDate',
            typeable: true,
            hasInputMask: true,
          },
        ] as TFilterConfig[];
      },
    },
    methods: {
      ...mapActions({
        getUpdateStatuses: INDICATOR_LOAD_MONITORING_ACTIONS_NAME.getUpdateStatuses,
        getSources: INDICATOR_LOAD_MONITORING_ACTIONS_NAME.getSources,
      }),
      async onDownload(filter: TFilter) {
        if (this.isDownloading) return;

        this.downloadMessage = '';
        this.isDownloading = true;

        try {
          const isSaved = await Utils.downloadFile(
            Link.importAnalytics.indicatorLoadMonitoring.getReports,
            REPORT_FILENAME,
            mapSortParams(filter),
          );

          if (!isSaved) {
            this.downloadMessage = 'Нет данных для выгрузки';
          }
        } catch (error) {
          const status = (error as { response?: { status?: number } })?.response?.status;
          this.downloadMessage =
            status === 500
              ? 'Сервер временно недоступен. Повторите попытку позже'
              : 'Не удалось сформировать файл. Проверьте фильтры';
        } finally {
          this.isDownloading = false;
        }
      },
      getRowClass(row: TTableData<TIndicatorRow>): string {
        return row?.isIntegration ? 'indicator-load-monitoring__row_integration' : '';
      },
    },
  });
